import { ButtonType } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as React from 'react';

import { FormikTextBox } from '~commonInfra/components/FormikField';
import { AuthenticationService } from '~src/authentication/services/AuthenticationService';
import { LoginPageStyles } from '~src/authentication/styles/LoginPageStyles';
import { Messages } from '~src/codegen/Localize';
import { ButtonWithSpinner } from '~src/common/react/components/ButtonWithSpinner';
import { IGetSiteSamlLoginInfoResult } from '~src/ts/ServerApi/types/IGetSiteSamlLoginInfoResult';

import { LoginErrorMessage } from './LoginErrorMessage';
import { RootLoginUiProps } from './RootLoginUi';

export interface SiteSpecificSamlLoginFormProps extends RootLoginUiProps {
  initialUsername: string;
  kickOffLoginForUser: (username: string, siteSamlInfo: IGetSiteSamlLoginInfoResult) => void;
}

interface SiteSpecificSamlLoginFormFields {
  username: string;
}

function hasUsername(username: string): string | undefined {
  if (!username) {
    return Messages.enterYourUsername();
  }
}

export function SiteSpecificSamlLoginForm(props: SiteSpecificSamlLoginFormProps) {

  const [isFetchingUserData, setIsFetchingUserData] = React.useState(false);
  const [errorCode, setErrorCode] = React.useState(props.signinQueryParams.error ?? 0);

  function fetchAuthTypeForUser(username: string, formik: FormikHelpers<any>): void {
    setIsFetchingUserData(true);
    setErrorCode(0);

    AuthenticationService.siteSamlLogin(username)
      .then((result: IGetSiteSamlLoginInfoResult) => {
        props.kickOffLoginForUser(username, result);
      }, (errorCode: number) => {
        setErrorCode(errorCode);
        setIsFetchingUserData(false);
        formik.resetForm();
        formik.setValues({
          username: username
        });
      });
  }

  return (
    <Formik
      initialValues={{ username: props.initialUsername }}
      onSubmit={(values: SiteSpecificSamlLoginFormFields, formik: FormikHelpers<any>) => {
        fetchAuthTypeForUser(values.username, formik);
      }}
      enableReinitialize={true}
    >
      {(formik: FormikProps<SiteSpecificSamlLoginFormFields>) => (
        <form onSubmit={formik.handleSubmit} data-tb-test-id='site-saml-login-form'>
          <div>
            <div className='tb-login-text-box tb-padded'>
              <FormikTextBox
                className={LoginPageStyles.textboxStyle}
                name='username'
                testId='site-saml-username'
                tb-test-id='textbox-username-input' // Maintaining legacy test id to be backwards compatible
                label={Messages.username()}         // for existing test automation.
                required={true}
                validate={hasUsername}
                onClear={() => formik.setFieldValue('username', '')}
              />
            </div>
            <LoginErrorMessage errorCode={errorCode} />
          </div>
          <ButtonWithSpinner
            className={LoginPageStyles.submitButtonStyle}
            type='submit'
            buttonType={ButtonType.Primary}
            disabled={isFetchingUserData}
            showSpinner={isFetchingUserData}
            title={isFetchingUserData ? Messages.signingIn() : Messages.signIn()}
            tb-test-id='button-signin' // Maintaining legacy test id to be backwards compatible for existing test automation.
            testId='site-saml-username-submit' />
      </form>
      )}
    </Formik>
  );
}
